import type * as Monaco from 'monaco-editor'

interface Table {
    name: string
    fields: Array<{
        name: string
        type: string
    }>
}
// 'id SERIAL PRIMARY KEY'
export const getTableDataFromQuery = (query: string): Table => {
    const [, tableName, fieldsString] = /^\s*create\s+table\s+"?(\w+)"?\s*\(([^]*)\)[\s;]*$/i.exec(query) || []
    const fields = fieldsString.split(',').map(field => {
        const [,name, type] = /^"?(\w+)"?\s+(.+)$/.exec(field.trim()) || ['', '']
        return { name, type }
    })
    return {
        name: tableName,
        fields
    }
}

export const buildMonacoPostgresqlFunctions = (range: Monaco.IRange, monaco: typeof Monaco): Monaco.languages.CompletionItem[] => [
    // --- AGGREGATE FUNCTIONS ---
    {
        label: 'AVG',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'AVG(${1:expression})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Calculates the average of non-null input values.',
        range,
    },
    {
        label: 'COUNT',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'COUNT(${1:* | expression})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Counts the number of input rows or non-null values.',
        range,
    },
    {
        label: 'MAX',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'MAX(${1:expression})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Computes the maximum of non-null input values.',
        range,
    },
    {
        label: 'MIN',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'MIN(${1:expression})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Computes the minimum of non-null input values.',
        range,
    },
    {
        label: 'SUM',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'SUM(${1:expression})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Computes the sum of non-null input values.',
        range,
    },
    {
        label: 'STRING_AGG',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'STRING_AGG(${1:value}, ${2:delimiter})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Concatenates non-null input values into a string, separated by the delimiter.',
        range,
    },
    {
        label: 'ARRAY_AGG',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'ARRAY_AGG(${1:expression})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Aggregates all non-null input values into an array.',
        range,
    },
    // --- WINDOW FUNCTIONS (Also Aggregates when used with OVER) ---
    {
        label: 'ROW_NUMBER',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'ROW_NUMBER() OVER (${1:PARTITION BY ... ORDER BY ...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the number of the current row within its partition, counting from 1.',
        range,
    },
    {
        label: 'RANK',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'RANK() OVER (${1:ORDER BY ...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the rank of the current row with gaps (skipping rank values for ties).',
        range
    },
    {
        label: 'DENSE_RANK',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'DENSE_RANK() OVER (${1:ORDER BY ...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the rank of the current row without gaps (consecutive ranking for ties).',
        range
    },
    {
        label: 'LEAD',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'LEAD(${1:column_name}, ${2:offset}, ${3:default}) OVER (${4:PARTITION BY ... ORDER BY ...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Accesses data from a subsequent row in the partition.',
        range
    },
    {
        label: 'LAG',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'LAG(${1:column_name}, ${2:offset}, ${3:default}) OVER (${4:PARTITION BY ... ORDER BY ...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Accesses data from a preceding row in the partition.',
        range
    },
    // --- STRING FUNCTIONS ---
    {
        label: 'CONCAT',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'CONCAT(${1:str1}, ${2:str2}, ${3:...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Concatenates the text representations of the arguments.',
        range
    },
    {
        label: 'LENGTH',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'LENGTH(${1:string})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the length of the string in characters.',
        range
    },
    {
        label: 'LOWER',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'LOWER(${1:string})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Converts string to all lowercase.',
        range
    },
    {
        label: 'UPPER',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'UPPER(${1:string})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Converts string to all uppercase.',
        range
    },
    {
        label: 'SUBSTRING',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'SUBSTRING(${1:string} FROM ${2:start} FOR ${3:length})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Extracts a substring.',
        range
    },
    {
        label: 'TRIM',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'TRIM(${1:string})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Removes leading/trailing spaces, or specified characters.',
        range
    },
    {
        label: 'REPLACE',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'REPLACE(${1:string}, ${2:from}, ${3:to})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Replaces all occurrences of a substring.',
        range
    },
    // --- DATE/TIME FUNCTIONS ---
    {
        label: 'NOW',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'NOW()',
        documentation: 'Returns the current transaction\'s start timestamp with time zone.',
        range
    },
    {
        label: 'CURRENT_DATE',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'CURRENT_DATE',
        documentation: 'Returns the current date.',
        range
    },
    {
        label: 'CURRENT_TIMESTAMP',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'CURRENT_TIMESTAMP',
        documentation: 'Returns the current date and time with time zone.',
        range
    },
    {
        label: 'EXTRACT',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'EXTRACT(${1:field} FROM ${2:source})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Extracts a subfield (e.g., YEAR, MONTH) from a date/time value.',
        range
    },
    {
        label: 'DATE_TRUNC',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'DATE_TRUNC(${1:field}, ${2:source})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Truncates a timestamp to a specified precision (e.g., \'month\').',
        range
    },
    {
        label: 'AGE',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'AGE(${1:timestamp1}, ${2:timestamp2})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Calculates the difference between two timestamps.',
        range
    },
    {
        label: 'TO_DATE',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'TO_DATE(${1:text}, ${2:format})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Converts a string to a date according to a format string.',
        range
    },
    // --- MATHEMATICAL FUNCTIONS ---
    {
        label: 'ABS',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'ABS(${1:number})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Calculates the absolute value.',
        range
    },
    {
        label: 'ROUND',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'ROUND(${1:number}, ${2:decimals})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Rounds a number to the nearest integer or specified decimal places.',
        range
    },
    {
        label: 'CEIL',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'CEIL(${1:number})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the smallest integer greater than or equal to the argument.',
        range
    },
    {
        label: 'FLOOR',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'FLOOR(${1:number})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the largest integer less than or equal to the argument.',
        range
    },
    {
        label: 'POWER',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'POWER(${1:base}, ${2:exponent})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Raises the base to the power of the exponent.',
        range
    },
    {
        label: 'RANDOM',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'RANDOM()',
        documentation: 'Returns a random floating-point value between 0.0 and 1.0.',
        range
    },
    // --- CONDITIONAL & UTILITY FUNCTIONS ---
    {
        label: 'COALESCE',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'COALESCE(${1:expression1}, ${2:expression2}, ${3:...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the first of its arguments that is not NULL.',
        range
    },
    {
        label: 'NULLIF',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'NULLIF(${1:value1}, ${2:value2})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns NULL if value1 equals value2, otherwise returns value1.',
        range
    },
    {
        label: 'GREATEST',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'GREATEST(${1:value1}, ${2:value2}, ${3:...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the largest value in a list of expressions.',
        range
    },
    {
        label: 'LEAST',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'LEAST(${1:value1}, ${2:value2}, ${3:...})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Returns the smallest value in a list of expressions.',
        range
    },
    {
        label: 'CAST',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'CAST(${1:expression} AS ${2:type})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Converts an expression to a different data type.',
        range
    },
    {
        label: 'GENERATE_SERIES',
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: 'GENERATE_SERIES(${1:start}, ${2:stop}, ${3:step})',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'Generates a series of values (numbers or timestamps).',
        range
    },
]

export const buildMonacoSqlKeywords = (range: Monaco.IRange, monaco: typeof Monaco): Monaco.languages.CompletionItem[] => [
    // --- Query Structure Keywords ---
    {
        label: 'SELECT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'SELECT ',
        documentation: 'Retrieves data from one or more tables.',
        range,
    },
    {
        label: 'FROM',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'FROM ',
        documentation: 'Specifies the table(s) to query from.',
        range,
    },
    {
        label: 'WHERE',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'WHERE ',
        documentation: 'Filters rows based on specified conditions.',
        range,
    },
    {
        label: 'HAVING',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'HAVING ',
        documentation: 'Filters groups after GROUP BY has been applied.',
        range,
    },
    {
        label: 'DISTINCT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'DISTINCT ',
        documentation: 'Removes duplicate rows from the result set.',
        range,
    },
    // --- JOIN Keywords ---
    {
        label: 'JOIN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'JOIN ',
        documentation: 'Combines rows from two or more tables (defaults to INNER JOIN).',
        range,
    },
    {
        label: 'INNER JOIN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'INNER JOIN ',
        documentation: 'Returns rows that have matching values in both tables.',
        range,
    },
    {
        label: 'LEFT JOIN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'LEFT JOIN ',
        documentation: 'Returns all rows from the left table and matched rows from the right table.',
        range,
    },
    {
        label: 'RIGHT JOIN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'RIGHT JOIN ',
        documentation: 'Returns all rows from the right table and matched rows from the left table.',
        range,
    },
    {
        label: 'FULL JOIN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'FULL JOIN ',
        documentation: 'Returns all rows when there is a match in either left or right table.',
        range,
    },
    {
        label: 'CROSS JOIN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'CROSS JOIN ',
        documentation: 'Returns the Cartesian product of rows from both tables.',
        range,
    },
    {
        label: 'ON',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'ON ',
        documentation: 'Specifies the condition for joining tables.',
        range,
    },
    {
        label: 'USING',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'USING ',
        documentation: 'Joins tables based on columns with the same name.',
        range,
    },
    // --- Grouping & Sorting ---
    {
        label: 'ORDER BY',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'ORDER BY ',
        documentation: 'Sorts the result set by specified columns.',
        range,
    },
    {
        label: 'BY',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'BY ',
        documentation: 'Used with ORDER BY or GROUP BY to specify ordering or grouping columns.',
        range,
    },
    {
        label: 'GROUP BY',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'GROUP BY ',
        documentation: 'Groups rows that have the same values in specified columns.',
        range,
    },
    {
        label: 'ASC',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'ASC',
        documentation: 'Sorts the result set in ascending order (default).',
        range,
    },
    {
        label: 'DESC',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'DESC',
        documentation: 'Sorts the result set in descending order.',
        range,
    },
    // --- Logical Operators ---
    {
        label: 'AND',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'AND ',
        documentation: 'Combines multiple conditions (all must be true).',
        range,
    },
    {
        label: 'OR',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'OR ',
        documentation: 'Combines multiple conditions (at least one must be true).',
        range,
    },
    {
        label: 'NOT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'NOT ',
        documentation: 'Negates a condition.',
        range,
    },
    // --- Comparison & Membership Operators ---
    {
        label: 'IN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'IN ',
        documentation: 'Checks if a value exists in a list of values.',
        range,
    },
    {
        label: 'NOT IN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'NOT IN ',
        documentation: 'Checks if a value does not exist in a list of values.',
        range,
    },
    {
        label: 'IS',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'IS ',
        documentation: 'Compares a value with NULL or boolean values.',
        range,
    },
    {
        label: 'IS NULL',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'IS NULL ',
        documentation: 'Checks if a value is NULL.',
        range,
    },
    {
        label: 'IS NOT NULL',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'IS NOT NULL ',
        documentation: 'Checks if a value is not NULL.',
        range,
    },
    {
        label: 'LIKE',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'LIKE ',
        documentation: 'Matches a pattern in a string (case-sensitive).',
        range,
    },
    {
        label: 'NOT LIKE',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'NOT LIKE ',
        documentation: 'Checks if a string does not match a pattern (case-sensitive).',
        range,
    },
    {
        label: 'ILIKE',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'ILIKE ',
        documentation: 'Matches a pattern in a string (case-insensitive, PostgreSQL).',
        range,
    },
    {
        label: 'BETWEEN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'BETWEEN ',
        documentation: 'Checks if a value is within a range (inclusive).',
        range,
    },
    {
        label: 'NOT BETWEEN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'NOT BETWEEN ',
        documentation: 'Checks if a value is not within a range.',
        range,
    },
    {
        label: 'EXISTS',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'EXISTS ',
        documentation: 'Checks if a subquery returns any rows.',
        range,
    },
    {
        label: 'NOT EXISTS',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'NOT EXISTS ',
        documentation: 'Checks if a subquery returns no rows.',
        range,
    },
    // --- Set Operations ---
    {
        label: 'UNION',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'UNION ',
        documentation: 'Combines results from multiple SELECT statements (removes duplicates).',
        range,
    },
    {
        label: 'UNION ALL',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'UNION ALL ',
        documentation: 'Combines results from multiple SELECT statements (keeps duplicates).',
        range,
    },
    {
        label: 'INTERSECT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'INTERSECT ',
        documentation: 'Returns rows that are common to both result sets.',
        range,
    },
    {
        label: 'EXCEPT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'EXCEPT ',
        documentation: 'Returns rows from the first query that are not in the second query.',
        range,
    },
    // --- Aliases & Common Table Expressions ---
    {
        label: 'AS',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'AS ',
        documentation: 'Assigns an alias to a column or table.',
        range,
    },
    {
        label: 'WITH',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'WITH ',
        documentation: 'Defines a common table expression (CTE).',
        range,
    },
    // --- Conditional Logic ---
    {
        label: 'CASE',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'CASE ',
        documentation: 'Starts a conditional expression.',
        range,
    },
    {
        label: 'WHEN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'WHEN ',
        documentation: 'Specifies a condition in a CASE expression.',
        range,
    },
    {
        label: 'THEN',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'THEN ',
        documentation: 'Specifies the result when a WHEN condition is true.',
        range,
    },
    {
        label: 'ELSE',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'ELSE ',
        documentation: 'Specifies the default result in a CASE expression.',
        range,
    },
    {
        label: 'END',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'END ',
        documentation: 'Ends a CASE expression or block.',
        range,
    },
    // --- PostgreSQL Specific (Limit/Offset) ---
    {
        label: 'LIMIT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'LIMIT ',
        documentation: 'Restricts the number of rows returned.',
        range,
    },
    {
        label: 'OFFSET',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'OFFSET ',
        documentation: 'Skips a specified number of rows before returning results.',
        range,
    },
]